import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Card, EmptyState, Skeleton } from '@/ui/primitives'
import { lei } from '@/lib/money'

type Sold = { ron?: number; eur?: number; income_ron?: number; income_eur?: number }
type Zi = {
  date: string; day_name: string
  opening_balance: Sold; income: Sold; expense: Sold; closing_balance: Sold
  entries: { id?: number; description?: string | null; amount?: number; currency?: string
    type?: string; created_at?: string | null; partner_name?: string | null }[]
  entries_count: number
}
type Sumar = {
  month: number; year: number
  totals: { income_ron: number; income_eur: number; expense_ron: number; expense_eur: number
    balance_ron: number; balance_eur: number }
  daily: { date: string; income_ron: number; income_eur: number; expense_ron: number
    expense_eur: number; count: number }[]
  days_with_transactions: number; total_transactions: number
}

const val = (s: Sold | undefined, cheie: 'ron' | 'eur') =>
  (s?.[cheie] ?? s?.[cheie === 'ron' ? 'income_ron' : 'income_eur'] ?? 0)
const zi = (s: string) => new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' })

export function CashRegister() {
  const azi = useQuery({ queryKey: ['casa-zi'], queryFn: () => api.get<Zi>('/cash-register/daily') })
  const luna = useQuery({ queryKey: ['casa-luna'], queryFn: () => api.get<Sumar>('/cash-register/summary') })

  return (
    <>
      <PageHeader
        title="Casa"
        count={luna.data ? `${luna.data.total_transactions} operatiuni in luna` : undefined}
      />
      <div className="px-8 py-6 space-y-6">
        <section>
          <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-3">
            Astazi {azi.data ? `· ${azi.data.day_name} ${zi(azi.data.date)}` : ''}
          </h2>
          {azi.isPending ? <Card><Skeleton rows={3} /></Card> : (
            <div className="grid gap-4 sm:grid-cols-4">
              {([
                ['Sold initial', azi.data!.opening_balance],
                ['Incasari', azi.data!.income],
                ['Plati', azi.data!.expense],
                ['Sold final', azi.data!.closing_balance],
              ] as const).map(([eticheta, s]) => (
                <Card key={eticheta} className="p-5">
                  <p className="text-[12.5px] text-muted">{eticheta}</p>
                  <p className="mt-1.5 tnum text-[21px] leading-none">{lei(val(s, 'ron'))} <span className="text-[12px] text-muted">lei</span></p>
                  <p className="mt-1 tnum text-[13px] text-muted">{lei(val(s, 'eur'))} EUR</p>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-3">Luna curenta</h2>
          <Card className="overflow-hidden">
            {luna.isPending ? <Skeleton /> : (luna.data?.daily ?? []).length === 0 ? (
              <EmptyState title="Nicio operatiune luna asta" />
            ) : (
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="text-left text-[11.5px] uppercase tracking-[0.1em] text-muted">
                    <th className="font-medium px-5 py-3">Ziua</th>
                    <th className="font-medium px-5 py-3 text-right">Incasari lei</th>
                    <th className="font-medium px-5 py-3 text-right">Incasari EUR</th>
                    <th className="font-medium px-5 py-3 text-right">Plati lei</th>
                    <th className="font-medium px-5 py-3 text-right">Operatiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {luna.data!.daily.map((d) => (
                    <tr key={d.date} className="border-t border-line">
                      <td className="px-5 py-2.5 tnum">{zi(d.date)}</td>
                      <td className="px-5 py-2.5 text-right tnum">{d.income_ron ? lei(d.income_ron) : <span className="text-muted">—</span>}</td>
                      <td className="px-5 py-2.5 text-right tnum">{d.income_eur ? lei(d.income_eur) : <span className="text-muted">—</span>}</td>
                      <td className="px-5 py-2.5 text-right tnum">{d.expense_ron ? lei(d.expense_ron) : <span className="text-muted">—</span>}</td>
                      <td className="px-5 py-2.5 text-right tnum text-muted">{d.count}</td>
                    </tr>
                  ))}
                  {luna.data && (
                    <tr className="border-t-2 border-line-strong font-medium">
                      <td className="px-5 py-3">Total</td>
                      <td className="px-5 py-3 text-right tnum">{lei(luna.data.totals.income_ron)}</td>
                      <td className="px-5 py-3 text-right tnum">{lei(luna.data.totals.income_eur)}</td>
                      <td className="px-5 py-3 text-right tnum">{lei(luna.data.totals.expense_ron)}</td>
                      <td className="px-5 py-3 text-right tnum text-muted">{luna.data.total_transactions}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </Card>
        </section>
      </div>
    </>
  )
}
